/**
 * Fait : les schémas de tout ce qu'on demande AU SUJET D'UN PROJET — le créer,
 *   le renommer, l'archiver, le partager, changer le niveau de quelqu'un — et
 *   les deux niveaux qu'un membre peut avoir dedans.
 *
 *   Sortis de `schemas.ts` le 2026-08-23, au lot 4 : le référent y a ajouté ce
 *   qu'il fallait pour faire déborder le fichier du plafond de lignes du dépôt,
 *   et les schémas de projet forment le groupe qui se lit seul — c'est celui
 *   qui part, comme les schémas du budget et du catalogue avant lui.
 *
 * Ne fait pas : il ne valide aucun droit et ne dit pas qui a le droit de quoi.
 *   Un schéma dit la FORME d'une demande ; `comptes/autorisation.ts`, côté
 *   serveur, dit qui peut la faire.
 */

import { z } from 'zod';

import {
  adresseElectronique,
  descriptionDonnee,
  identifiant,
  nomDonne,
  versionDeRessource,
} from './schemas.ts';

/** Ce qu'un membre peut faire dans un projet — le contrat public du partage. */
export const NIVEAUX_DE_MEMBRE = ['lecture', 'modification'] as const;

export type NiveauDeMembre = (typeof NIVEAUX_DE_MEMBRE)[number];

/** Partager un projet : à qui, et à quel niveau. */
export const demandeDePartage = z
  .object({ adresseElectronique, niveau: z.enum(NIVEAUX_DE_MEMBRE) })
  .strict();

export type DemandeDePartage = z.infer<typeof demandeDePartage>;

/** Changer le niveau d'un membre déjà partagé. */
export const demandeDeNiveau = z.object({ niveau: z.enum(NIVEAUX_DE_MEMBRE) }).strict();

export type DemandeDeNiveau = z.infer<typeof demandeDeNiveau>;

/** Ce que la création d'un projet accepte — un nom, rien d'autre. */
export const demandeDeProjet = z
  .object({
    nom: nomDonne,
    description: descriptionDonnee.optional(),

    /**
     * LE RÉFÉRENT, `D-023` — et il est **optionnel ICI, exigé par le
     * service**. Ce n'est pas un relâchement, c'est la seule place juste.
     *
     * Obligatoire dans ce schéma, un parent administrateur — qui n'a aucun
     * référent à fournir — recevrait « la demande est mal formée » : un
     * message faux, sur une demande parfaitement formée. Le service, lui,
     * connaît le rôle du demandeur, et peut donc dire la vraie raison et
     * NOMMER le choix attendu (critère 128).
     *
     * LA CHAÎNE VIDE EST ACCEPTÉE ICI, et vaut « pas de référent ». Trouvé le
     * 2026-08-23 par l'épreuve indépendante : un menu déroulant laissé sur son
     * entrée neutre envoie `''`, et le schéma le refusait — le demandeur
     * recevait donc « la demande est mal formée », c'est-à-dire exactement la
     * phrase que le critère 128 nomme comme défaut. Le cas « aucune clé
     * envoyée » était juste ; sa forme voisine, la plus probable à l'écran, ne
     * l'était pas. La normaliser ici plutôt que dans la route évite d'avoir
     * deux endroits qui décident ce qu'est un référent absent.
     */
    referentId: identifiant.or(z.literal('')).optional(),

    /**
     * Le niveau qu'on donne à son référent — lecteur ou éditeur, au choix du
     * créateur (`D-023`). Absent, c'est `lecture` : le plus petit droit qui
     * permette encore de voir ce qu'on suit.
     */
    niveauDuReferent: z.enum(NIVEAUX_DE_MEMBRE).optional(),
  })
  .strict();

export type DemandeDeProjet = z.infer<typeof demandeDeProjet>;

/**
 * Ce qu'une modification de projet accepte : le changement, ET la version
 * qu'on croit modifier. Sans elle, deux membres qui écrivent en même temps
 * s'effacent l'un l'autre (D-011, critère 21) — la version n'est donc pas
 * optionnelle, elle est la demande.
 */
export const demandeDeRenommage = z
  .object({
    nom: nomDonne,

    /**
     * La description accompagne le nom dans la MÊME demande, et suit donc le
     * même arbitrage par version. Les séparer aurait donné deux écritures
     * concurrentes sur un seul projet, chacune capable de faire échouer
     * l'autre pour une modification que l'utilisateur croyait unique.
     *
     * Absente, elle n'est pas touchée ; chaîne vide, elle est effacée. C'est
     * la seule façon d'exprimer « je retire ma description » sans inventer un
     * troisième geste.
     */
    description: descriptionDonnee.optional(),

    version: versionDeRessource,
  })
  .strict();

export type DemandeDeRenommage = z.infer<typeof demandeDeRenommage>;

/** Archiver ou désarchiver — même exigence de version. */
export const demandeDArchivage = z
  .object({ archive: z.boolean(), version: versionDeRessource })
  .strict();

export type DemandeDArchivage = z.infer<typeof demandeDArchivage>;
